import { css } from '@emotion/react';
import React, { forwardRef, useEffect, useRef } from 'react';

import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import { BaseInputProps } from './Input';
import {
  submitButtonContainer,
  textareaBase,
  textareaWithSubmit,
  variantError,
  variantSuccess,
  wrapperStyles,
} from './TextArea.styles';

export interface TextareaProps
  extends BaseInputProps,
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  /**
   * Number of visible text lines
   */
  rows?: number;
  /**
   * Whether the textarea can be resized
   */
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
}

type ExtraProps = {
  autoExpand?: boolean;
  maxHeight?: number | string;
  showSubmit?: boolean;
  onSubmit?: () => void;
  submitAriaLabel?: string;
  submitDisabled?: boolean;
  submitOnEnter?: boolean;
};

export const TextArea = forwardRef<
  HTMLTextAreaElement,
  TextareaProps & ExtraProps
>(
  (
    {
      rows = 3,
      variant = 'default',
      disabled = false,
      readOnly = false,
      className,
      autoExpand = true,
      maxHeight,
      resize = 'none',
      value,
      onChange,
      onKeyDown: onKeyDownProp,
      showSubmit = false,
      onSubmit,
      submitAriaLabel = 'Submit',
      submitDisabled,
      submitOnEnter = true,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    // Autosize only on user input to avoid measuring while hidden.
    useEffect(() => {
      const el = innerRef.current;
      if (!el || !autoExpand) return;
      // When value is cleared (e.g., after submit), reset inline height so rows applies
      if (value === '' || value === undefined || value === null) {
        el.style.height = '';
        el.style.overflowY = maxHeight ? 'auto' : 'hidden';
      }
    }, [value, autoExpand, maxHeight]);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
      if (autoExpand && innerRef.current) {
        const el = innerRef.current;
        if (typeof maxHeight === 'number') {
          el.style.maxHeight = `${maxHeight}px`;
        } else if (typeof maxHeight === 'string') {
          el.style.maxHeight = maxHeight;
        }
        el.style.height = 'auto';
        el.style.overflowY = maxHeight ? 'auto' : 'hidden';
        el.style.height = `${el.scrollHeight}px`;
      }
      onChange?.(e);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (
      e
    ) => {
      onKeyDownProp?.(e);
      if (e.defaultPrevented) return;
      const isEnter = e.key === 'Enter';
      const hasModifier = e.shiftKey || e.altKey || e.ctrlKey || e.metaKey;
      const isComposing = (e.nativeEvent as KeyboardEvent).isComposing;
      const canSubmit = !disabled && !readOnly && !submitDisabled;
      if (
        submitOnEnter &&
        showSubmit &&
        onSubmit &&
        isEnter &&
        !hasModifier &&
        !isComposing &&
        canSubmit
      ) {
        e.preventDefault();
        onSubmit();
      }
    };

    return (
      <div css={wrapperStyles}>
        <textarea
          ref={(node) => {
            innerRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref)
              (
                ref as React.MutableRefObject<HTMLTextAreaElement | null>
              ).current = node;
          }}
          rows={rows}
          disabled={disabled}
          readOnly={readOnly}
          css={[
            textareaBase,
            variant === 'error' && variantError,
            variant === 'success' && variantSuccess,
            css`
              resize: ${resize};
            `,
            showSubmit && textareaWithSubmit,
          ]}
          className={className}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          aria-multiline="true"
          {...props}
        />
        {showSubmit && (
          <div css={submitButtonContainer}>
            <Button
              aria-label={submitAriaLabel}
              onClick={onSubmit}
              disabled={disabled || submitDisabled}
              icon={
                <Icon
                  variant="PaperPlane"
                  size="large"
                  fill="var(--color-base-white)"
                />
              }
            />
          </div>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
